import React, { SyntheticEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';

import ModalBody from 'components/Common/MUIModal/ModalBody';
import ModalActions from 'components/Common/MUIModal/ModalActions';
import ProductBrand from 'components/ProductPageV2/ProductDetails/ProductBrand/ProductBrand';
import ProductGallery from 'components/ProductPageV2/ProductGallery/ProductGallery';
import ProductStickersAdapter from 'components/ProductPageV2/ProductDetails/ProductStickers/ProductStickersAdapter';
import ProductColors from 'components/ProductPageV2/ProductDetails/ProductColors/ProductColors';
import ProductPrice from 'components/ProductPageV2/ProductDetails/ProductPrice/ProductPrice';
import ProductSizes from 'components/ProductPageV2/ProductDetails/ProductSizes/ProductSizes';
import ProductAddBasket, {
  ProductAddBasketClasses,
} from 'components/ProductPageV2/ProductDetails/ProductAddBasket/ProductAddBasket';
import Button from 'components/UI/Buttons/Button/Button';
import SelectRender from './Select/SelectRender';
import { useProductSku } from 'hooks/ProductPageV2/useProductSku';
import { useProductStocks } from 'hooks/ProductPageV2/useProductStocks';
import {
  getBrandName,
  getGoToProduct,
  getLot,
  getName,
} from 'services/product';
import { getUri } from 'services/generic';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import { getProductData } from 'services/gtm/gtmPageView';
import { formatProductV2 } from 'services/gtm/gtmUtils';
import { Cart } from 'types/Cart';
import {
  FormattedProductDataType,
  ProductType,
  SelectedProductAttributes,
} from 'types/ProductPageV2/Products';

import styles from './ProductSheetBody.module.scss';

type Props = {
  formattedProduct: FormattedProductDataType;
  color?: string;
  setColor: (color: string) => void;
  onClose: () => void;
};

export default function ProductSheetBody({
  formattedProduct: {
    product,
    variants,
    sizes,
    cups,
    productAttributes,
    colors,
    variantsAttributes,
    productType,
  },
  color,
  setColor,
  onClose,
}: Props): JSX.Element {
  const { t } = useTranslation('checkout');
  const [size, setSize] = useState<string>();
  const [cup, setCup] = useState<string>();
  const [isSizeModalOpen, setSizeModalOpen] = useState(false);
  const brandName = getBrandName(productAttributes);
  const { stocks, isLoaded: stockIsLoaded } = useProductStocks(
    productAttributes.codeCouleur.Value
  );

  const formattedGtmProductData = useMemo(
    () => getProductData(formatProductV2(productAttributes)),
    [productAttributes]
  );

  const { selectedProduct } = useProductSku(
    product,
    variantsAttributes || {},
    productType,
    size,
    cup
  );

  const productUrl = useMemo(
    () => getGoToProduct(product, variants, selectedProduct, color),
    [selectedProduct, color, variants, product]
  );

  function handleGtmAddToCart(cart: Cart): void {
    pushGtmEvent(
      {
        event: '_add_to_cart',
        products: [
          {
            ...formattedGtmProductData,
            add_to_cart_type: 'panier - recommendation',
            quantity: 1,
          },
        ],
      },
      cart
    );
  }

  function handleGtmSeeProduct() {
    pushGtmEvent({
      event: '_click_other',
      category: 'clic autre - checkout',
      element: "n'oubliez pas",
      products: [{ ...formattedGtmProductData, quantity: 1 }],
    });
  }

  const renderAddBasket = (
    onSizeRequired?: boolean,
    classes?: Partial<ProductAddBasketClasses>
  ): JSX.Element => {
    return (
      <ProductAddBasket
        product={selectedProduct}
        qty={1}
        onSizeRequired={onSizeRequired}
        openModal={idModal => {
          if (idModal === 'productSize') setSizeModalOpen(true);
          if (idModal === 'addToMyBasket') onClose();
        }}
        closeModal={() => {
          setSizeModalOpen(false);
        }}
        gtmMandatoryAttributes={[]}
        sendGtmEvent={handleGtmAddToCart}
        classes={classes}
      />
    );
  };

  return (
    <>
      <ModalBody className={styles.modalBody} bottomDivider={false}>
        <ProductGallery
          productAttributes={productAttributes}
          classes={{ container: styles.galleryContainer }}
          disableZoomMode
        />
        <div className={styles.productInformation}>
          {brandName && <ProductBrand brandName={brandName} />}
          <h2 className={styles.productTitle}>{getName(productAttributes)}</h2>
          <ProductStickersAdapter
            productAttributes={productAttributes}
            classes={{ container: styles.stickers }}
          />
          <ProductPriceAdapter
            selectedProduct={selectedProduct}
            productAttributes={productAttributes}
            size={size}
          />
          <ProductColors
            classes={{ container: styles.colorsContainer }}
            colors={colors}
            color={color ?? productAttributes.couleur.Value}
            setColor={(color, e: SyntheticEvent) => {
              e.preventDefault(); // Disable redirect
              setColor(color);
            }}
            maxItems={4}
            breakpoints={{ 1200: 6 }}
          />
          <ProductSizes
            skus={variantsAttributes || {}}
            type={productType}
            sizes={sizes}
            cups={cups}
            size={size}
            setSize={size => setSize(size)}
            cup={cup}
            setCup={setCup}
            open={isSizeModalOpen}
            closeModal={() => setSizeModalOpen(false)}
            renderAddBasket={renderAddBasket}
            classes={{ container: styles.productSizes }}
            stocks={stocks}
            stockIsLoaded={stockIsLoaded}
            SelectRenderComponent={SelectRender}
          />
          {renderAddBasket(true, {
            addToCartButton: styles.addToCartButton,
          })}
          <div className={styles.viewProductWrapper}>
            <Button
              component={Link}
              to={getUri(productUrl)}
              onClick={handleGtmSeeProduct}
              className={styles.viewProductLink}
            >
              <span>{t('VIEW_PRODUCT')}</span>
              <ArrowForwardIosIcon className={styles.icon} />
            </Button>
          </div>
        </div>
      </ModalBody>
      <ModalActions className={styles.modalActions}>
        <ProductPriceAdapter
          selectedProduct={selectedProduct}
          productAttributes={productAttributes}
          size={size}
          mode={'lite'}
        />
        {renderAddBasket(true, {
          addToCartButton: styles.addToCartButton,
        })}
      </ModalActions>
    </>
  );
}

function ProductPriceAdapter({
  selectedProduct,
  productAttributes,
  size,
  mode = 'full',
}: {
  selectedProduct?: ProductType;
  productAttributes: SelectedProductAttributes;
  size?: string;
  mode?: 'full' | 'lite';
}): JSX.Element {
  return (
    <ProductPrice
      price={
        selectedProduct?.Attributes.prix.Value ?? productAttributes.prix?.Value
      }
      preDiscountPrice={
        selectedProduct?.Attributes.prixBarre.Value ??
        productAttributes.prixBarre?.Value
      }
      discountPercentage={
        selectedProduct?.Attributes.decote.Value ??
        productAttributes.decote?.Value
      }
      selectedSize={size}
      ecopart={productAttributes.ecopart?.Value}
      priceVolume={productAttributes.prixVolume?.Value}
      isUniquePrice={productAttributes.prixUnique.Value == '1'}
      redPrice={productAttributes.PRIXROUGE?.Value}
      liteMode={mode === 'lite'}
      lot={getLot(productAttributes)}
      classes={{ container: styles.priceContainer }}
    />
  );
}
